<template>
    <v-sheet class="px-4 px-lg-0 bg-transparent">
        <back-to-home />
        <card-skeleton :is-loading="isLoading" elevation="1" class-name="mb-8 rounded-lg px-2 py-2">
            <issue-details-header
                :id="Number(issueData?.id)"
                :subject="issueData?.subject"
                :icon="statusType.icon"
                :color="statusType.color"
                :is-loading="isLoading"
                :created-at="issueData?.occuredAt"
                :text="t(`${statusType.text}`)"
                :is-demand="issueData?.isDemand"
                :issue-sub-categories="issueData?.issueSubCategories"
                :issue-category="issueData?.issueCategory"
            />
            <issue-details-body :body="issueData?.body" :is-loading="isLoading" />
        </card-skeleton>
        <card-skeleton v-if="issueData?.attachments?.length" :is-loading="isLoading" elevation="1" class-name="mb-8 rounded-lg px-2 py-2">
            <issue-details-attachments :attachments="issueData?.attachments" :is-loading="isLoading" :issue-id="issueData?.id" />
        </card-skeleton>
        <card-skeleton class-name="bg-transparent mb-8 rounded-lg pa-0 overflow-visible">
            <issue-details-comments
                :id="Number(issueData?.id)"
                :issue-comment-count="issueCommentCount"
                :is-loading="isLoading"
                :issue-comments="issueComments"
            />
        </card-skeleton>
        <card-skeleton :is-loading="isLoading" elevation="1">
            <add-form v-if="issueData?.id" :issue-id="Number(issueData?.id)" :is-loading="isLoading" />
        </card-skeleton>
    </v-sheet>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { AddForm } from '@/features/Issues/components/form';
import { CommentDto, IssueSingleDto } from '@/features/issuesPage/types';
import { getIssueStatusType } from '@/features/Issues/helpers';
import { getSingleIssue } from '@/features/Issues/services/api';
import { useIssueCommentStore } from '@/features/Issues/store/cardStore';
import { IssueDetailsAttachments, IssueDetailsBody, IssueDetailsComments, IssueDetailsHeader } from '@/features/issuesPage';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import { BackToHome } from '@/features/common/components/page';

const route = useRoute();
const { t } = useI18n();

const issueCommentStore = useIssueCommentStore();
const { setItems } = issueCommentStore;
const { items } = storeToRefs(issueCommentStore);

const singleIssue = ref<IssueSingleDto>();
const isLoading = ref(false);

const issueCommentCount = computed(() => items.value?.length || 0);
const issueComments = computed<CommentDto[]>(() => items.value as CommentDto[]);
const issueData = computed(() => singleIssue.value?.data);
const statusType = computed(() => getIssueStatusType(issueData?.value?.status));

const getIssueDetails = async () => {
    try {
        isLoading.value = true;
        const id = Number(route.params.id);
        const response = await getSingleIssue(id);
        singleIssue.value = response;
        setItems(response?.data.comments);
    } catch (error) {
        singleIssue.value = undefined;
        setItems([]);
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    getIssueDetails();
});
</script>
